import React from 'react'
import { Flex, Center, Text, Button, Image, Tooltip, Box } from '@chakra-ui/react'

import GlossyDiamond from 'assets/images/glossy-diamond.svg'
import { moneyFormat } from 'utils/functions/formatNumber'
import { IOfferingsB } from 'services/IApi.d'
import CheckList from 'assets/icons/checkList'
import InfoCircle from 'assets/icons/infoCircle'

interface OfferingProps {
  title: string
  codeDN: string
  type: number
  price: number
  limits: {
    transferAmountLimitArs: number
    transferAmountLimitUsd: number
    transferQuantityLimit: number
    consultationLimit: number
  }
  selectedPlan: string
  action: (plan: IOfferingsB) => void
}

export const TYPE_OFFERING = {
  normal: 1,
  premium: 2,
}

const Offering = ({ title, price, limits, codeDN, type, selectedPlan, action }: OfferingProps) => {
  const isPremium = type === TYPE_OFFERING.premium
  const colorTitle = isPremium ? 'tertiary.80' : 'tertiary.500'
  const colorBody = isPremium ? 'tertiary.100' : 'neutral.20'

  return (
    <Flex
      data-testid='selected'
      flex='1'
      borderRadius={12}
      borderColor={selectedPlan === codeDN ? 'primary.500' : 'transparent'}
      borderWidth={3}
      p='3px'
    >
      <Flex
        flex='1'
        borderRadius={12}
        borderColor='secondary.80'
        borderWidth={1}
        overflow="hidden"
        data-testid={`offer-${title}`}
        flexDir='column'
      >
        <Box
          pt='32px'
          flexDir='column'
          pb={8}
          borderTopRadius="12px"
          borderBottomWidth="1px"
          bg={isPremium ? 'tertiary.20' : ''}
          borderBottomStyle='solid'
          borderBottomColor={isPremium ? 'tertiary.60' : 'secondary.80'}
          data-testid='planTitle'
        >
          <Center
            flexDir='column'
          >
            <Text textStyle='h4Bold' color={isPremium ? colorBody : colorTitle}>{title}</Text>
            <Text textStyle='h2Bold' mt={8} color={colorBody}>
              {price !== 0 ? moneyFormat(price, 'ARS') : 'Sin costo'}
            </Text>
            <Text
              textStyle='labelMdBold'
              color={colorBody}
              visibility={price ? 'visible' : 'hidden'}
            >
              +IVA por mes
            </Text>
            <Button
              mt={8}
              width='85%'
              data-testid={`select-button-${codeDN}`}
              onClick={() => action({
                codeDn: codeDN,
                denomination: title,
                price,
                transferAmountLimitArs: limits.transferAmountLimitArs,
                transferAmountLimitUsd: limits.transferAmountLimitUsd,
                transferQuantityLimit: limits.transferQuantityLimit,
                consultationLimit: limits.consultationLimit,
              })}
            >
              Seleccionar
            </Button>
          </Center>
        </Box>
        {!isPremium &&
          <Flex
            flexDir='column'
            my={8}
            mx={6}
            data-testid='limits-section'
          >
            <Text textStyle='labelLgBold' color='tertiary.500' py={4} ml={9}>Por mes podrás</Text>
            <Flex
              flexDir='row'
              pb={8}
              borderBottomStyle='solid'
              borderBottomWidth='1px'
              borderBottomColor='secondary.80'
            >
              <Flex flexDir='column' justifyContent='end' py={2}>
                <CheckList color='tertiary.500' width='24px' height='24px'/>
              </Flex>
              <Flex
                flexDir='column'
                ml={2}
              >
                <Text textStyle='labelSm' color='grey.100'>Transferir en pesos</Text>
                <Text textStyle='h6' color='neutral.20' my={2}>{moneyFormat(limits.transferAmountLimitArs, 'ARS')}</Text>
              </Flex>
            </Flex>
            <Flex
              flexDir='row'
              py={8}
              borderBottomStyle='solid'
              borderBottomWidth='1px'
              borderBottomColor='secondary.80'
            >
              <Flex flexDir='column' justifyContent='end' py={2}>
                <CheckList color='tertiary.500' width='24px' height='24px' />
              </Flex>
              <Flex
                flexDir='column'
                ml={2}
              >
                <Text textStyle='labelSm' color='grey.100'>Transferir en dólares</Text>
                <Text textStyle='h6' color='neutral.20' my={2}>{moneyFormat(limits.transferAmountLimitUsd, 'USD')}</Text>
              </Flex>
            </Flex>
            <Flex
              flexDir='row'
              py={8}
              borderBottomStyle='solid'
              borderBottomWidth='1px'
              borderBottomColor='secondary.80'
            >
              <Flex flexDir='column' justifyContent='end' py={2}>
              <CheckList color='tertiary.500' width='24px' height='24px' />
              </Flex>
              <Flex
                flexDir='column'
                ml={2}
              >
                <Text textStyle='labelSm' color='grey.100'>Enviar</Text>
                <Text textStyle='h6' color='neutral.20' my={2}>{limits.transferQuantityLimit} transferencias</Text>
              </Flex>
            </Flex>
            <Flex flexDir='row' py={8}>
              <Flex flexDir='column' justifyContent='end' py={2}>
              <CheckList color='tertiary.500' width='24px' height='24px' />
              </Flex>
              <Flex
                flexDir='column'
                ml={2}
              >
                <Text textStyle='labelSm' color='grey.100'>Descargar</Text>
                <Flex flexDir='row' alignItems='center'>
                  <Text textStyle='h6' color='neutral.20' my={2}>{limits.consultationLimit} reportes</Text>
                  <Tooltip placement='top' label='Los reportes incluyen información de tus movimientos, como transferencias, pagos y más'>
                    <Box>
                      <InfoCircle height='24px' width='24px' mx={2} />
                    </Box>
                  </Tooltip>
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        }
        {isPremium &&
          <Box bg='white' flex={1} flexDir='column' data-testid='premium-section' borderBottomRadius="12px">
            <Center mt={20}>
              <Image alt='Diamond image' src={GlossyDiamond}/>
            </Center>
            <Center my={4}>
              <Text align='center' textStyle='h6' color='neutral.20'>
                Transferí y descargá,<br />¡sin límites!
              </Text>
            </Center>
            <Center marginInlineEnd={4}>
              <Text align='center' textStyle='bodyMdSemi' color='neutral.20'>
                Aprovechá todo lo que<br />Interbanking tiene para<br />ofrecerte.
              </Text>
            </Center>
          </Box>
        }
      </Flex>
    </Flex>
  )
}

export default Offering